import { useCallback, useEffect, useState } from "react";
import { apiFetchOrThrow } from "../api/client";
import { Sentry } from "../lib/sentry";

export interface DeviceItem {
  id: string;
  platform: string | null;
  user_agent: string | null;
  country: string | null;
  city: string | null;
  last_used_at: string;
  created_at: string;
  current: boolean;
}

export function useDevices({ active = true }: { active?: boolean } = {}) {
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetchOrThrow<{ devices: DeviceItem[] }>(
        "/devices",
      );
      setDevices(data.devices);
    } catch (err) {
      // Offline / network errors are expected — show a friendly state.
      // Anything else is unexpected and worth surfacing.
      if (navigator.onLine) {
        Sentry.captureException(err);
      }
      setError(navigator.onLine ? "Failed to load devices" : "Offline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) void fetchDevices();
  }, [active, fetchDevices]);

  return { devices, loading, error, refetch: fetchDevices };
}

export function useRevokeDevice() {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (id: string, opts?: { force?: boolean }) => {
    setIsPending(true);
    try {
      const path = opts?.force ? `/devices/${id}?force=1` : `/devices/${id}`;
      await apiFetchOrThrow(path, { method: "DELETE" });
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
}
